import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { debounceTime, Observable, Subject, takeUntil } from 'rxjs';

import { AppState } from 'src/app/core/app.state';
import { INotificationItem } from '../../models/notification.models';
import {
  moduleLabelKey,
  titleKey,
  titleParams,
} from '../../services/notification-event-registry';
import { NotificationNavigatorService } from '../../services/notification-navigator.service';
import * as notificationActions from '../../store/notification.actions';
import {
  selectNotifications,
  selectNotificationsLoading,
  selectUnreadCount,
} from '../../store/notification.selectors';
import * as smartInboxActions from 'src/app/features/smart-inbox/store/smart-inbox.actions';

import { Actions, ofType } from '@ngrx/effects';

@Component({
  selector: 'app-notification-panel',
  templateUrl: './notification-panel.component.html',
  styleUrls: ['./notification-panel.component.scss'],
})
export class NotificationPanelComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter<void>();

  notifications$: Observable<INotificationItem[]>;
  loading$: Observable<boolean>;
  unreadCount$: Observable<number>;

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private navigator: NotificationNavigatorService,
  ) {
    this.notifications$ = this.store.select(selectNotifications);
    this.loading$ = this.store.select(selectNotificationsLoading);
    this.unreadCount$ = this.store.select(selectUnreadCount);
  }

  ngOnInit(): void {
    this.actions$.pipe(
      ofType(smartInboxActions.CommentMentionReceived, smartInboxActions.ThreadAssignmentReceived, smartInboxActions.OrderCreatedReceived),
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.store.dispatch(notificationActions.LoadNotifications({ params: { limit: 20 } }));
    });

    this.store.dispatch(notificationActions.LoadNotifications({ params: { limit: 20 } }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  onNotificationClick(notification: INotificationItem): void {
    if (!notification.is_read) {
      this.store.dispatch(notificationActions.MarkAsRead({ id: notification.id }));
    }
    this.navigator.navigate(notification);
    this.closed.emit();
  }

  onMarkAllAsRead(): void {
    this.store.dispatch(notificationActions.MarkAllAsRead());
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  getModuleLabel(notification: INotificationItem): string {
    return moduleLabelKey(notification);
  }

  getNotificationTitleKey(notification: INotificationItem): string {
    return titleKey(notification);
  }

  getNotificationTitleParams(notification: INotificationItem): Record<string, unknown> {
    return titleParams(notification);
  }

  preventClose(event: MouseEvent): void {
    event.stopPropagation();
  }

  timeAgo(dateStr: string): string {
    const now = Date.now();
    const date = new Date(dateStr).getTime();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 30) return `${diffDay}d ago`;
    return new Date(dateStr).toLocaleDateString();
  }
}
